import api from '@/lib/api';
import type { FullDiagnosticReport, RepairActionResult } from '../invoice-repair/types';
import type { SnapshotData } from './reconciliation-types';

// Long timeout for repair operations — large stores may have 3000+ invoices
const LONG_TIMEOUT = 600000;  // 10 min
const VERY_LONG_TIMEOUT = 3600000;  // 1h (renumber)

export const reconciliationApi = {
  fullDiagnostic: (dateFrom: string, dateTo: string, storeId?: string): Promise<FullDiagnosticReport & { dateFilterInfo?: { bySellDate: number; byIssueDate: number; combined: number } }> =>
    api.post('/invoice-repair/full-diagnostic', { dateFrom, dateTo, storeId }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  snapshot: (dateFrom: string, dateTo: string, storeId?: string): Promise<SnapshotData> =>
    api.get('/invoice-repair/snapshot', { params: { dateFrom, dateTo, storeId }, timeout: LONG_TIMEOUT }).then(r => r.data),

  bulkCreateMissing: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/bulk-create-missing', { dryRun, dateFrom, dateTo, storeId, forceOverLimit: true }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  importFromFakturownia: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/import-from-fakturownia', { dryRun, dateFrom, dateTo, storeId }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  deduplicate: (keepPrefix: string, dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/deduplicate', { keepPrefix, dryRun, dateFrom, dateTo, storeId }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  renumber: (departmentId: number, dryRun: boolean, dateFrom: string, dateTo: string, prefix?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/renumber', { departmentId, dryRun, dateFrom, dateTo, forceOverLimit: true, ...(prefix ? { prefix } : {}) }, { timeout: VERY_LONG_TIMEOUT }).then(r => r.data),

  fixPaymentDates: (dryRun: boolean, dateFrom: string, dateTo: string, departmentId?: number): Promise<RepairActionResult> =>
    api.post('/invoice-repair/fix-payment-dates', { dryRun, dateFrom, dateTo, departmentId }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  deleteCodReturned: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string, renumber?: boolean): Promise<RepairActionResult> =>
    api.post('/invoice-repair/delete-cod-returned', { dryRun, dateFrom, dateTo, storeId, renumber }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  deleteSurplus: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/delete-surplus', { dryRun, dateFrom, dateTo, storeId, forceOverLimit: true }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  fixTemp: (departmentId: number, dryRun: boolean, dateFrom: string, dateTo: string, prefix?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/fix-temp', { departmentId, dryRun, dateFrom, dateTo, forceOverLimit: true, ...(prefix ? { prefix } : {}) }, { timeout: VERY_LONG_TIMEOUT }).then(r => r.data),

  fixIssueDates: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/fix-issue-dates', { dryRun, dateFrom, dateTo, storeId }, { timeout: LONG_TIMEOUT }).then(r => r.data),

  syncLinks: (dryRun: boolean, dateFrom: string, dateTo: string, storeId?: string): Promise<RepairActionResult> =>
    api.post('/invoice-repair/sync-links', { dryRun, dateFrom, dateTo, storeId }, { timeout: LONG_TIMEOUT }).then(r => r.data),
};
